import { Modal, Grid, Box, Typography, Button, FormControl, TextField, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import React, { useState, useEffect } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { MuiTelInput, matchIsValidTel, MuiTelInputInfo, MuiTelInputCountry } from 'mui-tel-input';


type Option = { label: string; value: string };

export default function Support(props: any) {
    const { isOpen, setIsOpen, machine, settings } = props;
    const { t } = useTranslation();
    const [name, setName] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [phoneError, setPhoneError] = useState<boolean>(false);
    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<boolean>(false);
    const [productCategory, setProductCategory] = useState<Option[]>([]);
    const [selectedProductCategory, setSelectedProductCategory] = useState<string>("");
    const [deviceLocation, setDeviceLocation] = useState<Option[]>([]);
    const [selectedDeviceLocation, setSelectedDeviceLocation] = useState<string>("");
    const [problemDescription, setProblemDescription] = useState<string>("");
    const [supportOptionsLoaded, setSupportOptionsLoaded] = useState<boolean>(false);
    const [disableSupportButton, setDisableSupportButton] = useState<boolean>(true);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState<string>("");
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');
    const [sendScreenshot, setSendScreenshot] = useState<boolean>(false);
    const MY_HOST = import.meta.env.VITE_MY_HOST || "https://spectre-licensing.com";
    const [telCountry, setTelCountry] = useState<MuiTelInputCountry>('US');

    const toggleSupport = function () {
        return setIsOpen(!isOpen)
    }

    useEffect(() => {
        if (name && selectedProductCategory && selectedDeviceLocation && problemDescription && phoneNumber && !emailError && !phoneError) {
            setDisableSupportButton(false)
        } else {
            setDisableSupportButton(true)
        }
    }, [name, selectedProductCategory, selectedDeviceLocation, problemDescription, emailError, isOpen, phoneNumber, phoneError])

    useEffect(() => {
        console.log(settings)
        if (isOpen === true) {
            retrieveSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    // Load saved phone country preference on mount
    useEffect(() => {
        const savedCountry = localStorage.getItem('preferredPhoneCountry');
        if (savedCountry) {
            setTelCountry(savedCountry as MuiTelInputCountry);
        }
    }, [])

    const persistPreferredCountry = (country: MuiTelInputCountry) => {
        try {
            localStorage.setItem('preferredPhoneCountry', country);
        } catch (_) {
            // ignore localStorage errors (e.g., privacy mode)
        }
    }

    const retrieveSettings = function () {
        return fetch(`${MY_HOST}/api/HubspotSupportOptions`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
        })
            .then((data: any) => data.json())
            .then((data: any) => {
                return JSON.parse(data);
            })
            .then((data: any) => {
                let productOptions = Array.isArray(data?.issue?.options) ? data.issue.options : [];
                let locationOptions = Array.isArray(data?.location?.options) ? data.location.options : [];

                // Set default if empty
                if (!productOptions.length) {
                    productOptions = [{ label: 'Other', value: 'Other' }];
                }
                if (!locationOptions.length) {
                    locationOptions = [{ label: 'N/A', value: 'N/A' }];
                }

                setProductCategory(productOptions);
                setDeviceLocation(locationOptions);
                setSupportOptionsLoaded(true);
            })
            .catch(err => {
                console.log('retrieve settings failed', err);
                // Fallback options so the user can still submit a support request
                setProductCategory([{ label: 'Other', value: 'Other' }]);
                setDeviceLocation([{ label: 'N/A', value: 'N/A' }]);
                setSupportOptionsLoaded(true);
            })
    }

    useEffect(() => {
        if (supportOptionsLoaded === true) {
            let savedLocation = settings.Support?.Location?.value ?? null;
            savedLocation ? setSelectedDeviceLocation(savedLocation) : savedLocation = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supportOptionsLoaded])

    async function requestSupport(cmd: string) {
        setDisableSupportButton(true);

        return await machine.connection.post(commands[cmd]())
            .then((response: any) => {
                const code = response?.Responses?.RequestSupport?.code;
                if (code === "0") {
                    setIsOpen(false);
                    setToastMessage(t('control.centerControl.supportDialog.requestSubmitted') || "Support request submitted");
                    setToastSeverity('success');
                    setToastOpen(true);
                    setSelectedProductCategory("");
                    setProblemDescription("");
                    setTimeout(() => setToastOpen(false), 3000);
                    settings.Support.Location.value = selectedDeviceLocation;
                }
            })
            .catch(() => {
                setIsOpen(false);
                setToastMessage(t('control.centerControl.supportDialog.requestFailed') || "Error: request failed");
                setToastSeverity('error');
                setToastOpen(true);
                setTimeout(() => setToastOpen(false), 3000);
            })
    }

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setEmail(value);

        // Email validation regex pattern
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // Check if the entered email matches the email regex pattern
        if (value.length > 0) {
            setEmailError(!emailRegex.test(value));
        } else {
            setEmailError(false)
        }
    };

    const handlePhoneChange = (newValue: string, info?: MuiTelInputInfo) => {
        persistPreferredCountry(telCountry);
        setPhoneNumber(newValue);
        // Track selected country from mui-tel-input
        if (info?.countryCode) {
            setTelCountry(info.countryCode);
        }
        if (newValue.length > 0) {
            // Validate using mui-tel-input helper
            setPhoneError(!matchIsValidTel(newValue));
        } else {
            setPhoneError(false);
        }
    };

    const commands: any = {
        supportRequest: () => {
            return {
                "Requests": {
                    "RequestSupport": {
                        "Name": name,
                        "Email": email,
                        "Phone": phoneNumber,
                        "Problem": selectedProductCategory,
                        "Location": selectedDeviceLocation,
                        "Description": problemDescription,
                        "SendScreenshot": sendScreenshot
                    }
                }
            }
        }
    }

    const styles: any = {
        modal: {
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: {
                md: "65%",
                sm: '75%',
                xs: "95%"
            },
            height: {
                md: '70%',
                xs: '80%'
            },
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: {
                md: 4,
                xs: 2
            },
            overflow: 'auto',
            borderRadius: '16px'
        },
        closeButton: {
            fontWeight: 700,
            float: 'right'
        },
        submitButton: {
            fontWeight: 700,
            float: 'right'
        },
        dropDownSettingsBox: {
            backgroundColor: '#FFFFFF',
            textArea: {
                color: '#4B4E65'
            },
            mt: 1
        },
        textSettingBox: {
            backgroundColor: '#FFFFFF',
            textArea: {
                color: '#4B4E65',
            },
            mt: 1
        },
        text: {
            "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#808080",
            },
            input: {
                "&::placeholder": {
                    opacity: 1,
                },
            },
            mt: 1,
            mb: 2
        },
        supportText: {
            fontSize: 28,
            fontWeight: 500,
            color: '#262D6D',
        }
    }

    const menuItemStyle = { justifyContent: 'flex-start' };

    var selectProductOptions = Array.isArray(productCategory)
        ? productCategory.map((item, i) => (
            <MenuItem sx={menuItemStyle} key={i} value={item.value}>{item.label}</MenuItem>
        ))
        : [];

    var selectLocationOptions = Array.isArray(deviceLocation)
        ? deviceLocation.map((item, i) => (
            <MenuItem sx={menuItemStyle} key={i} value={item.value}>{item.label}</MenuItem>
        ))
        : [];

    return (
        <>
            <Modal
                open={isOpen}
                onClose={toggleSupport}
            >
                <Grid container>
                    <Box sx={styles.modal}>
                        <Grid container
                            direction="row"
                            justifyContent="space-between"
                        >
                            <Grid item md={6} xs={11}>
                                <Typography sx={styles.supportText}>{t('control.centerControl.supportDialog.title')}</Typography>
                            </Grid>
                            <Grid item md={6} xs={1}>
                                <Button className="Blue-Button" sx={styles.closeButton} style={{ float: 'right' }} onClick={toggleSupport}>{t('navBar.navigationButtons.close')}</Button>
                            </Grid>
                            {!supportOptionsLoaded ?
                                (
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="space-evenly"
                                        alignItems="center"
                                    >
                                        <CircularProgress size={100} sx={{ color: "#F48430" }} />
                                    </Grid>
                                )
                                :
                                (
                                    <Grid item md={12} sx={{ pt: 1 }} justifyContent='space-between'>
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            label={t('control.centerControl.supportDialog.nameField')}
                                            required
                                            value={name}
                                            sx={styles.text}
                                            onChange={(e: any) => { setName(e.target.value) }}
                                        />
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            label={t('control.centerControl.supportDialog.emailField')}
                                            value={email}
                                            sx={styles.text}
                                            error={emailError}
                                            onChange={(e: any) => { handleEmailChange(e) }}
                                        />
                                        <MuiTelInput
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            label={t('control.centerControl.supportDialog.phoneField')}
                                            required
                                            value={phoneNumber}
                                            sx={styles.text}
                                            defaultCountry={telCountry}
                                            onChange={handlePhoneChange}
                                            error={phoneError}
                                            helperText={phoneError ? (t('control.centerControl.supportDialog.invalidPhone')) : undefined}
                                        />
                                        <FormControl fullWidth>
                                            <TextField
                                                select
                                                sx={styles.text}
                                                size="small"
                                                label={t('control.centerControl.supportDialog.categoryField')}
                                                required
                                                defaultValue=""
                                                value={selectedProductCategory}
                                                SelectProps={{
                                                    sx: { textAlign: 'left' }
                                                }}
                                                onChange={(e: any) => {
                                                    setSelectedProductCategory(e.target.value)
                                                }}
                                            >
                                                {selectProductOptions}
                                            </TextField>
                                        </FormControl>
                                        <FormControl fullWidth>
                                            <TextField
                                                select
                                                size="small"
                                                sx={styles.text}
                                                label={t('control.centerControl.supportDialog.locationField')}
                                                required
                                                defaultValue=""
                                                value={selectedDeviceLocation}
                                                SelectProps={{
                                                    sx: { textAlign: 'left' }
                                                }}
                                                onChange={(e: any) => {
                                                    setSelectedDeviceLocation(e.target.value)
                                                }}
                                            >
                                                {selectLocationOptions}
                                            </TextField>
                                        </FormControl>
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            multiline
                                            rows={4}
                                            placeholder={`${t('control.centerControl.supportDialog.descriptionPlaceholder') || 'Problem Description'}*`}
                                            value={problemDescription}
                                            sx={styles.text}
                                            onChange={(e: any) => { setProblemDescription(e.target.value) }}
                                            required
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={sendScreenshot}
                                                    onChange={(e) => setSendScreenshot(e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label={t('control.centerControl.supportDialog.sendScreenshot') || 'Send Screenshot?'}
                                            sx={{ mb: 2 }}
                                        />
                                    </Grid>
                                )}
                            <Grid item md={12} sx={{ pt: 2 }}>
                                <Button className="Orange-Button" sx={styles.submitButton} disabled={disableSupportButton} onClick={() => { requestSupport('supportRequest') }}>{t('control.centerControl.supportDialog.submitButton')}</Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Modal>
            <Modal open={toastOpen}>
                <Snackbar
                    open={toastOpen}
                    autoHideDuration={3000}
                    onClose={() => setToastOpen(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} sx={{ width: '100%' }}>
                        {toastMessage}
                    </Alert>
                </Snackbar>
            </Modal>
        </>
    )
}